import React, { memo, useCallback, useState } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { InsetModal as Modal } from '../../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { colors, spacing } from '../../../theme';
import { CustomTemplate, HeaderAsset } from '../types/messageForm.types';
import { LocationPicker } from '../../LocationPicker';
import { apiService } from '../../../services/api';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

interface Props {
  template: CustomTemplate;
  templateParameters: Record<number, string>;
  onParameterChange: (position: number, value: string) => void;
  headerAsset?: HeaderAsset;
  onAssetChange?: (asset: HeaderAsset) => void;
  missingPositions?: number[];
  campaignName?: string;
  businessId?: string;
  businessName?: string;
}

type Segment =
  | { type: 'text'; value: string }
  | { type: 'param'; position: number }
  | { type: 'bold'; value: string }
  | { type: 'strike'; value: string };

function buildLineSegments(line: string): Segment[] {
  const segments: Segment[] = [];
  const regex = /\{\{(\d+)\}\}|\*([^*]+)\*|~([^~]+)~/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: line.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) {
      segments.push({ type: 'param', position: parseInt(match[1], 10) });
    } else if (match[2] !== undefined) {
      segments.push({ type: 'bold', value: match[2] });
    } else if (match[3] !== undefined) {
      segments.push({ type: 'strike', value: match[3] });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < line.length) {
    segments.push({ type: 'text', value: line.slice(lastIndex) });
  }
  return segments;
}

function buildLines(text: string): Segment[][] {
  return text.split('\n').map(buildLineSegments);
}

// ─── Param input ──────────────────────────────────────────────────────────────
interface ParamInputProps {
  position: number;
  value: string;
  isMissing: boolean;
  isHeader: boolean;
  bubbleBg: string;
  onChange: (position: number, value: string) => void;
}

const FONT_SIZE = 14;
const LINE_HEIGHT = 22;

const ParamInput = memo<ParamInputProps>(({
  position, value, isMissing, isHeader, bubbleBg, onChange,
}) => {
  const styles = useThemedStyles(getStyles);
  const isEmpty = !value.trim();
  const underlineColor = isMissing ? '#e74c3c' : isEmpty ? '#f39c12' : '#27ae60';

  return (
    <TextInput
      style={[
        styles.paramInput,
        isHeader && styles.paramInputBold,
        {
          backgroundColor: bubbleBg,
          borderBottomColor: underlineColor,
          color: isMissing ? '#c0392b' : isEmpty ? '#777' : '#1a6b3c',
        },
      ]}
      value={value}
      onChangeText={t => onChange(position, t)}
      placeholder="fill in"
      placeholderTextColor={isMissing ? '#e0826d' : '#bbb'}
      autoCorrect={false}
      autoCapitalize="none"
      blurOnSubmit={false}
      multiline
      scrollEnabled={false}
    />
  );
});

// ─── Segment row ──────────────────────────────────────────────────────────────
interface SegmentRowProps {
  text: string;
  isHeader: boolean;
  templateParameters: Record<number, string>;
  missingPositions: number[];
  bubbleBg: string;
  onChange: (position: number, value: string) => void;
}

const SegmentRow = memo<SegmentRowProps>(({
  text, isHeader, templateParameters, missingPositions, bubbleBg, onChange,
}) => {
  const styles = useThemedStyles(getStyles);
  const lines = buildLines(text);
  return (
    <View>
      {lines.map((segments, lineIdx) => (
        <View key={lineIdx} style={styles.segmentRow}>
          {segments.length === 0
            ? <Text style={styles.segmentText}>{'\u200B'}</Text>
            : segments.map((seg, i) => {
                if (seg.type === 'text') {
                  return (
                    <Text key={i} style={[styles.segmentText, isHeader && styles.segmentBold]}>
                      {seg.value}
                    </Text>
                  );
                }
                if (seg.type === 'bold') {
                  return (
                    <Text key={i} style={[styles.segmentText, styles.boldText, isHeader && styles.segmentBold]}>
                      {seg.value}
                    </Text>
                  );
                }
                if (seg.type === 'strike') {
                  return (
                    <Text key={i} style={[styles.segmentText, styles.strikeText, isHeader && styles.segmentBold]}>
                      {seg.value}
                    </Text>
                  );
                }
                return (
                  <ParamInput
                    key={`p${seg.position}`}
                    position={seg.position}
                    value={templateParameters[seg.position] || ''}
                    isMissing={missingPositions.includes(seg.position)}
                    isHeader={isHeader}
                    bubbleBg={bubbleBg}
                    onChange={onChange}
                  />
                );
              })
          }
        </View>
      ))}
    </View>
  );
});

// ─── Asset card (inline in bubble) ────────────────────────────────────────────
interface AssetCardProps {
  headerType: 'image' | 'video' | 'document' | 'location';
  headerAsset?: HeaderAsset;
  isParameterized?: boolean;
  campaignName: string;
  businessId?: string;
  onAssetChange: (asset: HeaderAsset) => void;
}

/** The template header's file / place picker (upload image, video, document; pick a location) — also used by workflows' template steps. */
export const AssetCard = memo<AssetCardProps>(({
  headerType, headerAsset, isParameterized = false, campaignName, onAssetChange,
}) => {
  const styles = useThemedStyles(getStyles);
  const [isUploading, setIsUploading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const uploadMedia = async (uri: string, fileName: string, mimeType: string, type: HeaderAsset['type']) => {
    setIsUploading(true);
    try {
      const response = await apiService.uploadMedia(uri, fileName, mimeType, 'default-whatsapp');
      if (response.statusCode === 1000 && response.data) {
        let mediaUrl = response.data.mediaUrl;
        if (!mediaUrl.includes('/api/storage/media/proxy')) {
          const base = (process.env.EXPO_PUBLIC_API_URL || 'https://api.connect.dislix.com').replace(/\/$/, '');
          mediaUrl = `${base}/api/storage/media/proxy?url=${encodeURIComponent(mediaUrl)}&mimeType=${encodeURIComponent(mimeType)}`;
        }
        onAssetChange({ type, mediaId: response.data.mediaId, url: mediaUrl, isParameterized });
      } else {
        throw new Error(response.errors?.[0]?.message || 'Upload failed');
      }
    } catch (e: any) {
      Alert.alert('Upload Error', e.message || 'Failed to upload');
    } finally {
      setIsUploading(false);
    }
  };

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true, aspect: [16, 9], quality: 0.8,
    });
    if (!result.canceled && result.assets?.[0]) {
      const asset = result.assets[0];
      const fileName = asset.uri.split('/').pop() || `image_${Date.now()}.jpg`;
      await uploadMedia(asset.uri, fileName, 'image/jpeg', 'image');
    }
  };

  const pickVideo = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Videos, quality: 0.8,
    });
    if (!result.canceled && result.assets?.[0]) {
      const asset = result.assets[0];
      const fileName = asset.uri.split('/').pop() || `video_${Date.now()}.mp4`;
      await uploadMedia(asset.uri, fileName, 'video/mp4', 'video');
    }
  };

  const pickDocument = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: ['application/pdf', 'application/msword', 'application/vnd.ms-excel'],
    });
    if (!result.canceled && result.assets?.[0]) {
      const doc = result.assets[0];
      await uploadMedia(doc.uri, doc.name || `doc_${Date.now()}.pdf`, doc.mimeType || 'application/pdf', 'document');
    }
  };

  const handlePress = () => {
    if (isUploading) return;
    if (headerType === 'image') pickImage();
    else if (headerType === 'video') pickVideo();
    else if (headerType === 'document') pickDocument();
    else if (headerType === 'location') setShowLocationPicker(true);
  };

  const iconName = headerType === 'image' ? 'image-outline'
    : headerType === 'video' ? 'videocam-outline'
    : headerType === 'document' ? 'document-outline'
    : 'location-outline';

  const label = headerType === 'image' ? 'Tap to upload image'
    : headerType === 'video' ? 'Tap to upload video'
    : headerType === 'document' ? 'Tap to upload document'
    : 'Tap to select location';

  return (
    <>
      <TouchableOpacity style={styles.assetCard} onPress={handlePress} activeOpacity={0.7}>
        {isUploading ? (
          <View style={styles.assetCardInner}>
            <ActivityIndicator size="small" color="#075E54" />
            <Text style={styles.assetCardLabel}>Uploading…</Text>
          </View>
        ) : headerAsset?.url && headerType === 'image' ? (
          <View style={styles.assetCardInner}>
            <Image source={{ uri: headerAsset.url }} style={styles.assetImage} resizeMode="cover" />
            <View style={styles.assetChangeOverlay}>
              <Ionicons name="camera" size={18} color="#fff" />
              <Text style={styles.assetChangeText}>Change</Text>
            </View>
          </View>
        ) : headerAsset && headerType === 'video' ? (
          <View style={styles.assetCardInner}>
            <Ionicons name="play-circle" size={36} color="#075E54" />
            <Text style={styles.assetCardLabel}>Video selected · tap to change</Text>
          </View>
        ) : headerAsset && headerType === 'document' ? (
          <View style={styles.assetCardInner}>
            <Ionicons name="document" size={36} color="#075E54" />
            <Text style={styles.assetCardLabel}>Document selected · tap to change</Text>
          </View>
        ) : headerAsset?.mediaId && headerType === 'location' ? (
          <View style={styles.assetCardInner}>
            <Ionicons name="location" size={36} color="#075E54" />
            <Text style={styles.assetCardLabel}>Location selected · tap to change</Text>
          </View>
        ) : (
          <View style={styles.assetCardInner}>
            <Ionicons name={iconName} size={28} color="#aaa" />
            <Text style={styles.assetCardLabel}>{label}</Text>
          </View>
        )}
      </TouchableOpacity>

      {showLocationPicker && (
        <Modal visible animationType="slide" onRequestClose={() => setShowLocationPicker(false)}>
          <LocationPicker
            visible
            onLocationSelect={(loc) => {
              onAssetChange({ type: 'location', mediaId: JSON.stringify(loc), isParameterized });
              setShowLocationPicker(false);
            }}
            onClose={() => setShowLocationPicker(false)}
          />
        </Modal>
      )}
    </>
  );
});

// ─── Main component ────────────────────────────────────────────────────────────
const BUBBLE_BG = '#DCF8C6';

export const WhatsAppMessagePreviewEditable: React.FC<Props> = ({
  template,
  templateParameters,
  onParameterChange,
  headerAsset,
  onAssetChange,
  missingPositions = [],
  campaignName = 'campaign',
  businessId,
  businessName = 'Your Business',
}) => {
  const styles = useThemedStyles(getStyles);
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes().toString().padStart(2, '0');
  const timeStr = `${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;

  const handleChange = useCallback(
    (pos: number, val: string) => onParameterChange(pos, val),
    [onParameterChange],
  );

  const hasAssetSlot = !!(template.headerAsset?.type && onAssetChange);
  const headerType = template.headerAsset?.type as AssetCardProps['headerType'] | undefined;

  return (
    <View style={styles.outerContainer}>
      {/* WhatsApp header */}
      <View style={styles.chatHeader}>
        <View style={styles.avatarCircle}>
          <Text style={styles.avatarLetter}>B</Text>
        </View>
        <View style={styles.chatHeaderText}>
          <Text style={styles.chatName}>{businessName}</Text>
          <Text style={styles.chatOnline}>WhatsApp Business</Text>
        </View>
      </View>

      {/* Hint */}
      <View style={styles.hintRow}>
        <Text style={styles.hintText}>Tap the underlined areas to fill in your values</Text>
      </View>

      <View style={styles.wallpaper}>
        <View style={styles.bubbleWrapper}>
          <View style={styles.bubble}>

            {/* Inline asset card — only when template expects a media header */}
            {hasAssetSlot && headerType ? (
              <AssetCard
                headerType={headerType}
                headerAsset={headerAsset}
                isParameterized={template.headerAsset?.isParameterized}
                campaignName={campaignName}
                businessId={businessId}
                onAssetChange={onAssetChange!}
              />
            ) : headerAsset?.url ? (
              <Image source={{ uri: headerAsset.url }} style={styles.headerImage} resizeMode="cover" />
            ) : null}

            {template.templateHeader && !headerAsset?.url ? (
              <SegmentRow
                text={template.templateHeader}
                isHeader
                templateParameters={templateParameters}
                missingPositions={missingPositions}
                bubbleBg={BUBBLE_BG}
                onChange={handleChange}
              />
            ) : null}

            <SegmentRow
              text={template.templateBody || ''}
              isHeader={false}
              templateParameters={templateParameters}
              missingPositions={missingPositions}
              bubbleBg={BUBBLE_BG}
              onChange={handleChange}
            />

            {/* Footer + timestamp on the same row */}
            <View style={styles.footerRow}>
              {template.templateFooter ? (
                <Text style={styles.footerText} numberOfLines={1}>{template.templateFooter}</Text>
              ) : <View style={styles.footerSpacer} />}
              <View style={styles.timestampInline}>
                <Text style={styles.timestamp}>{timeStr}</Text>
                <Text style={styles.ticks}> ✓✓</Text>
              </View>
            </View>

          </View>
          <View style={styles.bubbleTail} />
        </View>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  outerContainer: {
    borderRadius: spacing.borderRadius.lg,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.borderMedium,
    backgroundColor: '#fff',
    marginBottom: 12,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#075E54',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 10,
  },
  avatarCircle: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: '#128C7E',
    justifyContent: 'center', alignItems: 'center',
  },
  avatarLetter: { color: '#fff', fontWeight: '700', fontSize: 16 },
  chatHeaderText: { flex: 1 },
  chatName: { color: '#fff', fontWeight: '600', fontSize: 15 },
  chatOnline: { color: 'rgba(255,255,255,0.72)', fontSize: 12 },
  hintRow: {
    backgroundColor: '#f0f9f4',
    paddingHorizontal: 12, paddingVertical: 6,
    borderBottomWidth: 1, borderBottomColor: '#d4edd9',
  },
  hintText: { fontSize: 12, color: '#2e7d50', fontStyle: 'italic' },
  wallpaper: { backgroundColor: '#ECE5DD', padding: 16, minHeight: 100 },
  bubbleWrapper: { maxWidth: '95%', alignSelf: 'flex-end', position: 'relative' },
  bubble: {
    backgroundColor: BUBBLE_BG,
    borderRadius: 12, borderTopRightRadius: 2,
    padding: 10, paddingBottom: 6,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1, shadowRadius: 2, elevation: 2,
  },
  bubbleTail: {
    position: 'absolute', top: 0, right: -8,
    width: 0, height: 0,
    borderTopWidth: 10, borderLeftWidth: 10,
    borderTopColor: BUBBLE_BG, borderLeftColor: 'transparent',
  },
  headerImage: { width: '100%', height: 150, borderRadius: 8, marginBottom: 8 },

  // ─── Asset card ─────────────────────────────────────────────────────────────
  assetCard: {
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 8,
    backgroundColor: '#e8f5e9',
    borderWidth: 1.5,
    borderColor: '#a5d6a7',
    borderStyle: 'dashed',
    minHeight: 80,
    justifyContent: 'center',
  },
  assetCardInner: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
  },
  assetImage: {
    width: '100%',
    height: 140,
  },
  assetChangeOverlay: {
    position: 'absolute',
    bottom: 6, right: 6,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderRadius: 12,
    paddingHorizontal: 8, paddingVertical: 3,
    gap: 4,
  },
  assetChangeText: { fontSize: 11, color: '#fff', fontWeight: '600' },
  assetCardLabel: { fontSize: 12, color: '#555', textAlign: 'center' },

  // ─── Segment ────────────────────────────────────────────────────────────────
  segmentRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  segmentText: { fontSize: FONT_SIZE, color: '#111', lineHeight: LINE_HEIGHT },
  segmentBold: { fontWeight: '700' },
  boldText: { fontWeight: '700' },
  strikeText: { textDecorationLine: 'line-through' },

  // ─── Footer + timestamp row ─────────────────────────────────────────────────
  footerRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginTop: 4,
    gap: 6,
  },
  footerText: { fontSize: 12, color: '#667781', lineHeight: 16, flex: 1 },
  footerSpacer: { flex: 1 },
  timestampInline: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
  },
  timestamp: { fontSize: 11, color: '#667781' },
  ticks: { fontSize: 11, color: '#53BDEB', fontWeight: '600' },

  // ─── Param input ────────────────────────────────────────────────────────────
  paramInput: {
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    minWidth: 52,
    maxWidth: '100%',
    flexShrink: 1,
    flexGrow: 1,
    minHeight: LINE_HEIGHT,
    paddingHorizontal: 2,
    paddingVertical: 0,
    marginHorizontal: 1,
    borderBottomWidth: 1.5,
  },
  paramInputBold: { fontWeight: '700' },
});
